import { getBaseUrl, request } from 'src/helper/api';

// ---- Users (people) ----

/**
 * `GET api/users` — the Settings Users list. Still returns the LEGACY,
 * `account_info`-wrapped item shape (`UserListItem` in `types.ts`) — the same
 * endpoint `user-management/api.ts`'s `getUsers` hits, kept as its own thunk
 * here so this slice owns its own loading/error state and cache.
 */
export const getUsersList = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/users`,
      contentType: 'json',
      token,
      signal,
    },
    'settings-users/getUsersList',
  );

/**
 * `GET api/users/{userId}` — the Settings Users detail read.
 */
export const getUserDetails = (userId?: string, token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/users/${userId}`,
      contentType: 'json',
      token,
      signal,
    },
    'settings-users/getUserDetails',
    // Threaded through to the fulfilled payload (same technique as
    // `getUsersForGroup`'s `_group_key`) so the reducer knows which `source`
    // entry to merge into — the URL has `userId` baked in, but the response
    // body itself carries no id.
    { _user_id: userId },
  );

/**
 * `PATCH api/users/{userId}/details` — the one write this feature knows. Wires
 * the consolidated endpoint only; deliberately no fan-out to the 4 legacy
 * per-section endpoints.
 *
 * Matches the repo's own thunk shape (`editUserData`, `updateTechnicianSkill`):
 * this returns the thunk, called with the `UpdateUserDetailsPayload` at dispatch
 * time — `dispatch(updateUserDetails(userId, token)(payload))`.
 */
export const updateUserDetails = (userId?: string, token?: string) =>
  request(
    {
      method: 'PATCH',
      url: `${getBaseUrl()}api/users/${userId}/details`,
      contentType: 'json',
      token,
    },
    'settings-users/updateUserDetails',
    { _user_id: userId },
  );

export const resetUserPassword = (userId?: string, token?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/users/${userId}/actions/resetPassword`,
      contentType: 'json',
      token,
    },
    'settings-users/resetUserPassword',
  );

export const setTemporaryPassword = (userId?: string, token?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/users/${userId}/actions/setTemporaryPassword`,
      contentType: 'json',
      token,
    },
    'settings-users/setTemporaryPassword',
  );

export const addUser = (token?: string | undefined) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/users`,
      contentType: 'json',
      token,
    },
    'settings-users/addUser',
  );

/**
 * `technicianId` is the TECHNICIAN uuid (`lookupData.technicians[].value`), not the
 * account's. Answers `SkillSetSection` unwrapped, and 404s when the technician has
 * no skills on file.
 */
export const importTechnicianSkills = (
  technicianId?: string,
  token?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/technicians/${technicianId}/skills`,
      contentType: 'json',
      token,
      signal,
    },
    'settings-users/importTechnicianSkills',
  );

/**
 * Vendor counterpart of `importTechnicianSkills`, assumed to mirror it exactly.
 * The route does not exist yet, so this 404s — which reads as "no skills to copy".
 */
export const importVendorSkills = (vendorId?: string, token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/vendors/${vendorId}/skills`,
      contentType: 'json',
      token,
      signal,
    },
    'settings-users/importVendorSkills',
  );

// ---- Roles (Settings → Users → User roles) ----

export const getRoles = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/roles`,
      contentType: 'json',
      token,
      signal,
    },
    'settings-users/roles/getRoles',
  );

export const getAllPermissions = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/permissions`,
      token,
      signal,
    },
    'settings-users/roles/getAllPermissions',
  );

export const addRolePermissions = (token?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/roles`,
      contentType: 'json',
      token,
    },
    'settings-users/roles/addRoleData',
  );

export const editRolePermissions = (role_Id?: string, token?: string) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/roles/${role_Id}`,
      contentType: 'json',
      token,
    },
    'settings-users/roles/editRolePermissions',
  );

export const getRolePermissions = (role_Id?: string, token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/roles/${role_Id}`,
      contentType: 'json',
      token,
      signal,
    },
    'settings-users/roles/editRoleData',
  );

export const deleteRole = (roleId?: string, token?: string) =>
  request(
    {
      method: 'DELETE',
      url: `${getBaseUrl()}api/roles/${roleId}`,
      contentType: 'json',
      token,
    },
    'settings-users/roles/deleteRole',
  );
